/**
 * Salary queries: the desk where an employee disputes their own pay and someone answers.
 *
 * THREE AUDIENCES, ONE ROUTER, SEPARATED BY PERMISSION AND NOT BY PATH. An employee reaches their
 * own tickets through `payroll.query.own:manage` at self scope. A manager holds `payroll.query:read`
 * at `reporting_tree` and sees their reports' tickets WITHOUT the internal thread and without the
 * ability to answer. The payroll desk holds `payroll.query:resolve` and can. `listQueries` returns
 * whichever of those the caller's grants support, with `scope` saying which — rather than three
 * near-identical endpoints that drift apart.
 *
 * THE INTERNAL NOTE IS FILTERED SERVER-SIDE. A thread returned to the subject of the ticket omits
 * every `internal_note` and every unsent draft. The rows are not sent and flagged; they are not
 * sent. A concealment the client performs is one network tab away from not being a concealment.
 *
 * THE PAYSLIP CONTEXT IS READ, NOT RETYPED. Opening a ticket returns the linked run's persisted
 * result for that employee — gross, TDS, the exemption applied — so the desk answers from the
 * ledger instead of from what the employee remembered. It is a projection of
 * `PayrollEmployeeResult`; nothing recalculates.
 *
 * RESOLVING SCHEDULES A PROMISE, IT DOES NOT MOVE MONEY. Ticking "adjust in the November run"
 * records the target run and the amount on the ticket and notifies the employee. The adjustment
 * itself is a payroll input on that run, made through the payroll-input module under its own
 * permissions and its own approval. A support desk that could write into a payroll run directly
 * would be a hole straight through the maker-checker separation the run module is built on.
 *
 * THE SLA CLOCK STOPS WHILE THE DESK IS WAITING ON THE EMPLOYEE, and nothing else stops it. See
 * PayrollSalaryQuery's model comment for why the accumulated pause is stored rather than replayed.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  CURRENCIES,
  SALARY_QUERY_MESSAGE_KINDS,
  SALARY_QUERY_PRIORITIES,
  SALARY_QUERY_RESOLUTIONS,
  SALARY_QUERY_STATES,
  SALARY_QUERY_TYPES,
} from '../enums.js';
import {
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const salaryQueryTypeSchema = z.enum(SALARY_QUERY_TYPES);
export const salaryQueryStateSchema = z.enum(SALARY_QUERY_STATES);
export const salaryQueryPrioritySchema = z.enum(SALARY_QUERY_PRIORITIES);
export const salaryQueryMessageKindSchema = z.enum(SALARY_QUERY_MESSAGE_KINDS);
export const salaryQueryResolutionSchema = z.enum(SALARY_QUERY_RESOLUTIONS);
const currencySchema = z.enum(CURRENCIES);

export const querySearchScopeSchema = z.enum(['own', 'team', 'all']);
export type QuerySearchScope = z.infer<typeof querySearchScopeSchema>;

const queryParams = z.object({ queryId: uuidSchema });

// ---------------------------------------------------------------------------
// Rows & detail
// ---------------------------------------------------------------------------

export const salaryQueryRowSchema = z.object({
  id: uuidSchema,
  ticketNo: z.string(),
  employmentId: uuidSchema,
  personId: uuidSchema,
  employeeName: z.string(),
  employeeCode: z.string().nullable(),
  designation: z.string().nullable(),
  departmentName: z.string().nullable(),
  queryType: salaryQueryTypeSchema,
  queryTypeLabel: z.string(),
  priority: salaryQueryPrioritySchema,
  state: salaryQueryStateSchema,
  periodLabel: z.string(),
  fiscalYear: z.number().int(),
  subject: z.string(),
  disputedAmount: decimalStringSchema.nullable(),
  currency: currencySchema.nullable(),
  assignedToPersonId: uuidSchema.nullable(),
  assignedToName: z.string().nullable(),
  raisedAt: instantSchema,
  slaDueAt: instantSchema.nullable(),
  /**
   * Hours left against the SLA, with the accumulated pause already deducted. Negative once it has
   * been breached. Null when the type carries no target.
   */
  slaHoursRemaining: decimalStringSchema.nullable(),
  isSlaBreached: z.boolean(),
  /** True while the clock is stopped because the desk is waiting on the employee. */
  isSlaPaused: z.boolean(),
  lastActivityAt: instantSchema,
  resolvedAt: instantSchema.nullable(),
  /** What THIS caller may do to THIS ticket. Decided server-side; the screen renders it. */
  canRespond: z.boolean(),
  canAssign: z.boolean(),
  canClose: z.boolean(),
});
export type SalaryQueryRow = z.infer<typeof salaryQueryRowSchema>;

export const salaryQueryMessageSchema = z.object({
  id: uuidSchema,
  kind: salaryQueryMessageKindSchema,
  authorPersonId: uuidSchema,
  authorName: z.string(),
  body: z.string(),
  isDraft: z.boolean(),
  createdAt: instantSchema,
});
export type SalaryQueryMessage = z.infer<typeof salaryQueryMessageSchema>;

/**
 * The payslip figures the ticket is about, from the persisted result of the linked run.
 *
 * Null when the ticket names a period this org has no calculated run for — which happens, and is
 * shown as "no payslip on file for that period" rather than as zeroes.
 */
export const linkedPayslipSchema = z.object({
  runId: uuidSchema,
  periodLabel: z.string(),
  grossEarnings: decimalStringSchema,
  taxDeducted: decimalStringSchema,
  totalDeductions: decimalStringSchema,
  netPayable: decimalStringSchema,
  currency: currencySchema,
  /** The standard deduction the regime applied, where the year's configuration defines one. */
  standardDeduction: decimalStringSchema.nullable(),
  /** Whether the employee's pay was withheld in that run — frequently the whole answer. */
  isHeld: z.boolean(),
});
export type LinkedPayslip = z.infer<typeof linkedPayslipSchema>;

/**
 * What the payroll engine can say about the dispute, assembled from facts already on record.
 *
 * NEVER A GENERATED EXPLANATION. Each note is built from a specific comparison the system can
 * actually make — a proof verified after the run's input cut-off, a diagnostic raised against this
 * employee in that run, an LOP day count. Where nothing applies, the list is empty and the desk
 * writes the answer itself. A plausible-sounding paragraph assembled from a template would be
 * worse than no note at all, because the desk would paste it into an official response.
 */
export const engineNoteSchema = z.object({
  code: z.string(),
  title: z.string(),
  detail: z.string(),
  amount: decimalStringSchema.nullable(),
});
export type EngineNote = z.infer<typeof engineNoteSchema>;

export const salaryQueryDetailSchema = salaryQueryRowSchema.extend({
  description: z.string(),
  resolution: salaryQueryResolutionSchema.nullable(),
  resolutionMessage: z.string().nullable(),
  resolvedByName: z.string().nullable(),
  closedAt: instantSchema.nullable(),
  escalatedAt: instantSchema.nullable(),
  escalatedToName: z.string().nullable(),
  adjustmentRunId: uuidSchema.nullable(),
  adjustmentPeriodLabel: z.string().nullable(),
  adjustmentAmount: decimalStringSchema.nullable(),
  linkedPayslip: linkedPayslipSchema.nullable(),
  engineNotes: z.array(engineNoteSchema),
  /** Internal notes and unsent drafts are absent for a caller who is the subject of the ticket. */
  messages: z.array(salaryQueryMessageSchema),
  /** The caller's own unsent draft on this ticket, when they have one. */
  draft: salaryQueryMessageSchema.nullable(),
  /** Runs still open for an adjustment to be scheduled into. */
  adjustmentTargets: z.array(
    z.object({ runId: uuidSchema, periodLabel: z.string(), payDate: z.string() }),
  ),
});
export type SalaryQueryDetail = z.infer<typeof salaryQueryDetailSchema>;

// ---------------------------------------------------------------------------
// Listing
// ---------------------------------------------------------------------------

export const listQueriesQuerySchema = z.object({
  /**
   * Which population to list. A caller asking for a scope their grants do not support gets a 403,
   * not a silently narrowed list — a screen that quietly showed fewer rows than asked for is how
   * someone concludes a ticket was deleted.
   */
  scope: querySearchScopeSchema.default('own'),
  state: salaryQueryStateSchema.optional(),
  queryType: salaryQueryTypeSchema.optional(),
  priority: salaryQueryPrioritySchema.optional(),
  legalEntityId: uuidSchema.optional(),
  assignedToPersonId: uuidSchema.optional(),
  /** Only tickets past their SLA. */
  breachedOnly: z.coerce.boolean().optional(),
  search: z.string().trim().max(120).optional(),
  sort: z.enum(['sla_urgent_first', 'newest', 'oldest']).default('sla_urgent_first'),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(100).default(25),
});

export const salaryQueryListSchema = z.object({
  items: z.array(salaryQueryRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  scope: querySearchScopeSchema,
  /** The scopes this caller may ask for, so the screen offers the tabs that will work. */
  availableScopes: z.array(querySearchScopeSchema),
});

/**
 * The tiles above the queue.
 *
 * `averageTurnaroundHours` IS OVER RESOLVED TICKETS ONLY and excludes paused time. An average that
 * counted open tickets as fast would improve every time the desk fell behind.
 */
export const salaryQuerySummarySchema = z.object({
  scope: querySearchScopeSchema,
  openCount: z.number().int().nonnegative(),
  priorityCount: z.number().int().nonnegative(),
  breachedCount: z.number().int().nonnegative(),
  awaitingEmployeeCount: z.number().int().nonnegative(),
  resolvedThisMonth: z.number().int().nonnegative(),
  averageTurnaroundHours: decimalStringSchema.nullable(),
  /** The open count per type, so the queue can be worked one kind of problem at a time. */
  byType: z.array(
    z.object({
      queryType: salaryQueryTypeSchema,
      label: z.string(),
      count: z.number().int().nonnegative(),
    }),
  ),
});
export type SalaryQuerySummary = z.infer<typeof salaryQuerySummarySchema>;

// ---------------------------------------------------------------------------
// Bodies
// ---------------------------------------------------------------------------

export const raiseQueryBodySchema = z.object({
  queryType: salaryQueryTypeSchema,
  /** The run being disputed. Omitted when the employee names a period with no run. */
  runId: uuidSchema.optional(),
  fiscalYear: z.number().int(),
  cycleNo: z.number().int().optional(),
  periodLabel: z.string().trim().min(1).max(64),
  subject: z.string().trim().min(3).max(200),
  description: z.string().trim().min(10).max(5000),
  disputedAmount: decimalStringSchema.optional(),
  currency: z.enum(CURRENCIES).optional(),
  /**
   * Raise on behalf of another employee — a desk logging a call. Requires `payroll.query:resolve`
   * over that employee; an ordinary employee passing it gets a 403 rather than a ticket in someone
   * else's name.
   */
  onBehalfOfEmploymentId: uuidSchema.optional(),
});

export const addMessageBodySchema = z.object({
  kind: salaryQueryMessageKindSchema,
  body: z.string().trim().min(1).max(5000),
  /** Save without sending. A draft is invisible to the employee whatever its kind says. */
  isDraft: z.boolean().default(false),
});

export const resolveQueryBodySchema = z.object({
  resolution: salaryQueryResolutionSchema,
  /** The official answer. Sent to the employee and kept on the ticket verbatim. */
  message: z.string().trim().min(10).max(5000),
  /** Schedule the correction into a named future run. Records a promise; moves no money. */
  scheduleAdjustment: z
    .object({
      runId: uuidSchema,
      amount: decimalStringSchema.optional(),
    })
    .optional(),
  notifyEmployee: z.boolean().default(true),
});

export const assignQueryBodySchema = z.object({
  assignedToPersonId: uuidSchema.nullable(),
  priority: salaryQueryPrioritySchema.optional(),
  note: z.string().trim().max(1000).optional(),
});

export const changeStateBodySchema = z.object({
  state: z.enum(['in_progress', 'awaiting_employee', 'open', 'closed', 'rejected']),
  note: z.string().trim().max(2000).optional(),
});

export const escalateQueryBodySchema = z.object({
  escalatedToPersonId: uuidSchema,
  reason: z.string().trim().min(5).max(2000),
});

export const slaReportQuerySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  fiscalYear: z.coerce.number().int().optional(),
  from: instantSchema.optional(),
  to: instantSchema.optional(),
});

/** Inline, like the payroll registers — see payroll-reports.contract.ts on why nothing is stored. */
export const slaReportSchema = z.object({
  fileName: z.string(),
  contentType: z.string(),
  content: z.string(),
  rowCount: z.number().int().nonnegative(),
  generatedAt: instantSchema,
});

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const payrollQueriesContract = c.router(
  {
    getSummary: {
      method: 'GET',
      path: '/salary-queries/summary',
      query: z.object({
        scope: querySearchScopeSchema.default('own'),
        legalEntityId: uuidSchema.optional(),
      }),
      responses: {
        200: salaryQuerySummarySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Open count, SLA breaches and turnaround for the chosen population',
      metadata: routeMeta([
        'payroll.query:read',
        'payroll.query:resolve',
        'payroll.query.own:manage',
      ]),
    },

    listQueries: {
      method: 'GET',
      path: '/salary-queries',
      query: listQueriesQuerySchema,
      responses: {
        200: salaryQueryListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'The queue, scoped to what the caller may see',
      metadata: routeMeta([
        'payroll.query:read',
        'payroll.query:resolve',
        'payroll.query.own:manage',
      ]),
    },

    getQuery: {
      method: 'GET',
      path: '/salary-queries/:queryId',
      pathParams: queryParams,
      responses: {
        200: salaryQueryDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One ticket: its thread, the linked payslip, and what the engine can say',
      metadata: routeMeta(null, { salaryPin: 'subject' }),
    },

    raiseQuery: {
      method: 'POST',
      path: '/salary-queries',
      headers: idempotencyHeadersSchema,
      body: raiseQueryBodySchema,
      responses: {
        201: salaryQueryDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Raise a query about a pay period',
      metadata: routeMeta(null),
    },

    addMessage: {
      method: 'POST',
      path: '/salary-queries/:queryId/messages',
      pathParams: queryParams,
      body: addMessageBodySchema,
      responses: {
        201: salaryQueryMessageSchema,
        401: errorBodySchema,
        /** An employee cannot write an internal note on their own ticket. */
        403: errorBodySchema,
        404: errorBodySchema,
        /** The ticket is closed. */
        409: errorBodySchema,
      },
      summary: 'Add a message, an internal note, or save a draft',
      metadata: routeMeta(null),
    },

    assignQuery: {
      method: 'POST',
      path: '/salary-queries/:queryId/assign',
      pathParams: queryParams,
      body: assignQueryBodySchema,
      responses: {
        200: salaryQueryDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Route a ticket to someone, and optionally re-prioritise it',
      metadata: routeMeta('payroll.query:resolve', { salaryPin: 'others' }),
    },

    changeState: {
      method: 'POST',
      path: '/salary-queries/:queryId/state',
      pathParams: queryParams,
      body: changeStateBodySchema,
      responses: {
        200: salaryQueryDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Not a legal transition from where the ticket currently is. */
        409: errorBodySchema,
      },
      summary: 'Move a ticket, pausing or resuming the SLA clock as the state requires',
      metadata: routeMeta('payroll.query:resolve', { salaryPin: 'others' }),
    },

    resolveQuery: {
      method: 'POST',
      path: '/salary-queries/:queryId/resolve',
      pathParams: queryParams,
      headers: idempotencyHeadersSchema,
      body: resolveQueryBodySchema,
      responses: {
        200: salaryQueryDetailSchema,
        401: errorBodySchema,
        /** Never satisfiable against one's own ticket — see can.ts's self-excluded permissions. */
        403: errorBodySchema,
        404: errorBodySchema,
        /** Already resolved, or the run named for the adjustment is no longer open. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Send the official answer and close the ticket out',
      metadata: routeMeta('payroll.query:resolve', { salaryPin: 'others' }),
    },

    escalateQuery: {
      method: 'POST',
      path: '/salary-queries/:queryId/escalate',
      pathParams: queryParams,
      body: escalateQueryBodySchema,
      responses: {
        200: salaryQueryDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Escalate a ticket to a named person, with a reason',
      metadata: routeMeta('payroll.query:resolve', { salaryPin: 'others' }),
    },

    exportSlaReport: {
      method: 'POST',
      path: '/salary-queries/sla-report',
      body: slaReportQuerySchema,
      responses: {
        200: slaReportSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Turnaround and breach counts, generated inline',
      metadata: routeMeta(['payroll.query:read', 'payroll.query:resolve'], { ai: 'read' }),
    },
  },
  { pathPrefix: '/payroll' },
);
